import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import AppSwitcherIcon from "@atlaskit/icon/glyph/app-switcher";
import RemoteSwitcherPlugin from "@atlassian/remote-module-loader/plugin-switcher";
import { RemoteComponent } from "@atlassian/remote-module-loader/remote-component";
import useRemoteModule from "@atlassian/remote-module-loader/use-remote-module";
import AtlassianSwitcher, { ExportedDataProvider } from "@atlassian/switcher";
import { useBxpWebStargateClient } from "@atlassiansox/bxp-web-stargate-client";
import { util } from "./util";
import {
    createAvailableProductsProvider,
    createJoinableSitesProvider
} from "@atlassian/switcher/create-custom-provider";
import {
    JoinableSite,
    JoinableSitesResponse
} from "@atlassian/switcher/dist/types/types";
import React, { useState, useMemo, useEffect } from "react";
import { IntlProvider } from "react-intl-next";

import {
    AppSwitcherContainer,
    AppSwitcherMenu,
    StyledAppSwitcherIcon,
    RemoteSwitcherContainer
} from "./helper-component";

export interface WacAppSwitcherProps {
    appSwitcherLabel: string;
    allowJoinSitesProvider: boolean;
}

type JoinableSitesResponseJson = {
    joinableSites?: Array<JoinableSite>;
};

const AVATAR_IMG_URL =
    "http://wac-cdn.atlassian.com/dam/wac/icons/ADG3-Icons-36px-N700/object-avatar-man.svg";

type SwitcherComponentProps = React.ComponentProps<typeof AtlassianSwitcher>;

const remoteSwitcherPlugin = new RemoteSwitcherPlugin({
    env: process.env["NODE_ENV"] === "production" ? "production" : "staging",
    variant: "latest"
});
const RemoteSwitcherPreloader = () => {
    useRemoteModule(remoteSwitcherPlugin);

    return null;
};

// eslint-disable-next-line complexity
export const WacAppSwitcher = ({
    appSwitcherLabel,
    allowJoinSitesProvider
}: WacAppSwitcherProps) => {
    const [hasHovered, setHasHovered] = useState(false);
    const [isAppSwitcherMenu, setAppSwitcherMenu] = useState(false);
    const [locale, setLocale] = useState("en");
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
    const [joinableSites, setJoinableSites] =
        useState<ExportedDataProvider<JoinableSitesResponse>>();
    const [isFirstSwitcherOpen, setIsFirstSwitcherOpen] = useState(true);
    const customAvailableProductsDataProvider = createAvailableProductsProvider(
        `${process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"]}/available-products/api/available-products`
    );
    const stargateClient = useBxpWebStargateClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClickOutside = (event: MouseEvent) => {
        const path = event.composedPath && event.composedPath();
        const insideAppswitcher = Array.prototype.includes.call(
            Array.prototype.map.call(path, (el: { id: string }) => {
                return el.id === "global-nav--wac__app-switcher";
            }),
            true
        );
        if (!insideAppswitcher) {
            setAppSwitcherMenu(false);
        }
    };

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.code == "Escape" && isAppSwitcherMenu) {
            setAppSwitcherMenu(false);
        }
    };

    useEffect(() => {
        async function getUserProfile() {
            const userProfile = await stargateClient.me({});
            return userProfile;
        }
        getUserProfile()
            .then(() => {
                setIsUserLoggedIn(true);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);
    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        document.addEventListener("keydown", handleKeydown);
        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("keydown", handleKeydown);
        };
    });

    useEffect(() => {
        setLocale(document.documentElement.lang);
    }, []);

    useEffect(() => {
        if (util.getCookie("login_user_detected") === "true")
            if (allowJoinSitesProvider) {
                getJoinableSites();
            }
    }, []);

    useEffect(() => {
        if (isAppSwitcherMenu) {
            createAnalyticsEvent({
                name: "WacAppSwitcher",
                action: "clicked",
                label: "WacAppSwitcherMenuRendered"
            }).fire();
        }
    }, [isAppSwitcherMenu]);

    const toggleSwitcherDropdown = async (
        allowWacJoinSitesProvider: boolean
    ) => {
        if (isFirstSwitcherOpen) {
            setIsFirstSwitcherOpen(false);
        } else if (joinableSites === undefined) {
            // reload the joinable sites data if a joinable site has been clicked
            if (allowWacJoinSitesProvider) {
                getJoinableSites();
            }
        }

        if (isAppSwitcherMenu) {
            // user closing switcher
            createAnalyticsEvent({
                name: "WacAppSwitcherIconClickedToClose",
                action: "clicked",
                label: "Closing the switcher"
            }).fire();
        }
        setAppSwitcherMenu(!isAppSwitcherMenu);
    };
    const getJoinableSites = async () => {
        const sites: JoinableSitesResponseJson = await fetch(
            `${process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"]}/joinable-sites`,
            {
                method: "POST",
                credentials: "include",
                body: JSON.stringify({
                    products: [
                        "confluence.ondemand",
                        "jira-software.ondemand",
                        "jira-servicedesk.ondemand",
                        "jira-core.ondemand"
                    ]
                }),
                headers: {
                    "Content-type": "application/json; charset=UTF-8"
                }
            }
        ).then((res) => res.json());
        const reMappedSitesData = (sites.joinableSites || []).map(
            // eslint-disable-next-line complexity
            (site: JoinableSite) => {
                const userData: Record<
                    string,
                    [{ avatarUrl: string; displayName: string }]
                > = {};
                Array.prototype.forEach.call(
                    site.products,
                    (val: string | number) => {
                        userData[val] = [
                            { avatarUrl: AVATAR_IMG_URL, displayName: "" }
                        ];
                    }
                );
                return {
                    cloudId: site.cloudId || "",
                    displayName: site.displayName || "",
                    url: site.url || "",
                    avatarUrl: site.avatarUrl,
                    relevance: site.relevance,
                    users: userData
                };
            }
        );

        const joinableSitesPromiseMaker = () => {
            return new Promise<{ sites: JoinableSite[] }>((resolve) => {
                setTimeout(() => {
                    resolve({ sites: reMappedSitesData });
                });
            });
        };
        setJoinableSites(
            createJoinableSitesProvider(joinableSitesPromiseMaker)
        );
    };

    const switcherProps = useMemo<SwitcherComponentProps>(
        () => ({
            product: "WAC",
            appearance: "standalone",
            joinableSitesDataProvider: joinableSites,
            availableProductsDataProvider: customAvailableProductsDataProvider,
            onJoinableSiteClicked: () => {
                setAppSwitcherMenu(false);

                // joinable sites might now change if user follows through joining this site
                // so dump joinable sites data to indicate it must be reloaded
                setJoinableSites(undefined);
            }
        }),
        [joinableSites, customAvailableProductsDataProvider]
    );
    const appSwitcher = (
        <AppSwitcherMenu>
            <RemoteComponent<SwitcherComponentProps>
                remotePlugin={remoteSwitcherPlugin}
                analyticsChannel="navigation"
                ErrorFallback={<AtlassianSwitcher {...switcherProps} />}
                {...switcherProps}
                product="WAC"
            />
        </AppSwitcherMenu>
    );

    if (!isUserLoggedIn) {
        return null;
    }

    return (
        <IntlProvider locale={locale || "en"}>
            <AppSwitcherContainer>
                <StyledAppSwitcherIcon
                    allowJoinSitesProvider={allowJoinSitesProvider}
                    appSwitcherLabel={appSwitcherLabel}
                    toggleSwitcherDropdown={toggleSwitcherDropdown}
                    setHasHovered={setHasHovered}
                >
                    <AppSwitcherIcon
                        size="large"
                        primaryColor="#0747a6"
                        label="App Switcher"
                    />
                </StyledAppSwitcherIcon>
                <RemoteSwitcherContainer display={isAppSwitcherMenu}>
                    {hasHovered && <RemoteSwitcherPreloader />}
                    {hasHovered && appSwitcher}
                </RemoteSwitcherContainer>
            </AppSwitcherContainer>
        </IntlProvider>
    );
};
